import { z } from 'zod';
import { activitySchema } from './activity.js';
import { commentSchema } from './comment.js';
import { cursorQuerySchema, dateOnlySchema, idSchema, softDeleteTimestampsSchema, taskPrioritySchema, taskSizeSchema, taskStatusSchema, utcDateTimeSchema, } from './common.js';
export const taskTitleSchema = z.string().min(1).max(200);
export const taskDescriptionSchema = z.string().max(20000);
/** La tarea tal y como la devuelve el API. */
export const taskSchema = z
    .object({
    id: idSchema,
    projectId: idSchema,
    /** Un solo nivel de jerarquía: si tiene padre, no puede tener hijas. */
    parentId: idSchema.nullable(),
    title: taskTitleSchema,
    description: taskDescriptionSchema.nullable(),
    status: taskStatusSchema,
    size: taskSizeSchema.nullable(),
    priority: taskPrioritySchema,
    assigneeId: idSchema.nullable(),
    dueDate: dateOnlySchema.nullable(),
    /** Orden dentro de su columna. Lo calcula el backend. */
    position: z.number(),
    createdBy: idSchema,
    completedAt: utcDateTimeSchema.nullable(),
})
    .extend(softDeleteTimestampsSchema.shape);
/**
 * Lo que necesita una tarjeta del tablero sin pedir el detalle: etiquetas y
 * contadores agregados.
 */
export const taskCardSchema = taskSchema.extend({
    labelIds: z.array(idSchema),
    commentCount: z.number().int().min(0),
    subtaskCount: z.number().int().min(0),
    subtaskDoneCount: z.number().int().min(0),
});
export const createTaskRequestSchema = z.object({
    projectId: idSchema,
    title: taskTitleSchema,
    description: taskDescriptionSchema.nullable().optional(),
    parentId: idSchema.nullable().optional(),
    status: taskStatusSchema.default('todo'),
    size: taskSizeSchema.nullable().optional(),
    priority: taskPrioritySchema.default('normal'),
    assigneeId: idSchema.nullable().optional(),
    dueDate: dateOnlySchema.nullable().optional(),
    /** Opcional: si no viene, la tarea se coloca al final de su columna. */
    position: z.number().optional(),
    labelIds: z.array(idSchema).optional(),
});
/**
 * `PATCH /tasks/{id}`. Es parcial y también sirve para mover de columna
 * (`status` + `position`). `createdBy`, `completedAt` y las marcas de tiempo
 * no se tocan desde fuera.
 */
export const updateTaskRequestSchema = z
    .object({
    projectId: idSchema,
    title: taskTitleSchema,
    description: taskDescriptionSchema.nullable(),
    parentId: idSchema.nullable(),
    status: taskStatusSchema,
    size: taskSizeSchema.nullable(),
    priority: taskPrioritySchema,
    assigneeId: idSchema.nullable(),
    dueDate: dateOnlySchema.nullable(),
    position: z.number(),
    labelIds: z.array(idSchema),
})
    .partial()
    .refine((value) => Object.keys(value).length > 0, {
    message: 'Se requiere al menos un campo',
});
/**
 * `POST /tasks/{id}/move`. El frontal manda la posición destino; el backend
 * calcula la posición real y renumera la columna si hace falta.
 */
export const moveTaskRequestSchema = z.object({
    status: taskStatusSchema,
    position: z.number(),
});
/** `GET /tasks`. Todos los filtros son combinables. */
export const listTasksQuerySchema = cursorQuerySchema.extend({
    projectId: idSchema.optional(),
    assigneeId: idSchema.optional(),
    status: taskStatusSchema.optional(),
    size: taskSizeSchema.optional(),
    priority: taskPrioritySchema.optional(),
    labelId: idSchema.optional(),
    parentId: idSchema.optional(),
    q: z.string().min(1).max(200).optional(),
    dueBefore: dateOnlySchema.optional(),
    dueAfter: dateOnlySchema.optional(),
    /**
     * Recorta solo la columna de finalizadas: una tarea en `done` entra si su
     * `completedAt` es igual o posterior a esta marca; las pendientes y en
     * proceso no se ven afectadas. Es lo que usa el tablero para "últimos 7/30
     * días" (fase 7).
     */
    doneSince: utcDateTimeSchema.optional(),
    includeDeleted: z.stringbool().default(false),
    onlyDeleted: z.stringbool().default(false),
});
/** `DELETE /tasks/{id}?cascade=true` */
export const deleteTaskQuerySchema = z.object({
    cascade: z.stringbool().default(false),
});
/**
 * `GET /tasks/{id}`. La tarjeta más las hijas (un solo nivel, ver CLAUDE.md §4),
 * el hilo de comentarios y el histórico de actividad — todo lo que pide el
 * panel de detalle en una sola petición.
 */
export const taskDetailSchema = taskCardSchema.extend({
    children: z.array(taskCardSchema),
    comments: z.array(commentSchema),
    activity: z.array(activitySchema),
});
//# sourceMappingURL=task.js.map